import type { components } from '../../lib/api/schema.gen'

type CartLinePresentation = components['schemas']['CartLinePresentation']
type ShipMode = components['schemas']['ShipMode']

export interface LineDateDisplay {
  /** The date shown as the row's headline ship date. */
  readonly primaryDateLabel: string | null
  /** Set only in ship-together mode, only when this line's own ship date is
   * strictly earlier than the order-level (held-back) date — the prototype's
   * "Could ship this early — deferred to ship everything together" case. */
  readonly earlyReadyDateLabel?: string
}

export interface DeriveLineDateDisplayInput {
  readonly mode: ShipMode
  readonly presentation: CartLinePresentation | undefined
  /** The order-level SHIP date the whole shipment goes out on in ship-together
   * mode, or null before one is known. Ignored in ship-as-ready mode. The
   * caller (`CartPage`) derives this as the LATEST line ship date — the order
   * can't leave until its slowest line is ready — so this stays ship-side and
   * the held-back comparison below is genuinely ship-vs-ship (code-review W1;
   * it must NOT be fed an arrival estimate, which over-reports held-back
   * lines by the transit interval). */
  readonly orderShipDate: string | null
  /** A BARE date label for `orderShipDate`, in the same "Wed, Aug 19" style
   * as every other date in this column (`CartLinePresentation.shipDateLabel`)
   * — NOT `CartSummary.arrivalEstimateLabel` ("Arrives Wed, Aug 19 · One
   * shipment"), which is the order-summary rail's copy and the wrong
   * granularity for a per-row cell (code-review UID-96 follow-up). Callers
   * without their own bare label for `orderShipDate` should build one with
   * `formatBareShipDateLabel`. */
  readonly orderShipDateLabel: string | null
}

/**
 * A bare "Wed, Aug 19" label from an ISO (`YYYY-MM-DD`) date — the same
 * convention the BFF already uses for `CartLinePresentation.shipDateLabel`
 * (`apps/api/src/modules/cart/cart-derivation.ts`'s `shipDateLabel`) and the
 * prototype's `_fmtDowShort` (`Fictiv Platform.dc.html` line ~7293, the
 * formatter behind the per-line `l.shipDate`/`l.readyDate` shown at line
 * ~1852). The caller uses it to label the order-level ship date (the latest
 * line ship date, code-review W1) in the same bare style as the per-line
 * dates; `CartSummary.arrivalEstimateLabel` is a pre-formatted full
 * order-summary string ("Arrives … · One shipment") and is NOT reusable as a
 * per-row date, so this formats the bare ISO date instead. UTC-based, matching
 * every other cart date formatter in this vertical (avoids local-timezone drift).
 */
export function formatBareShipDateLabel(isoDate: string): string {
  const WEEKDAYS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat']
  const MONTHS = [
    'Jan',
    'Feb',
    'Mar',
    'Apr',
    'May',
    'Jun',
    'Jul',
    'Aug',
    'Sep',
    'Oct',
    'Nov',
    'Dec',
  ]
  const d = new Date(`${isoDate}T00:00:00Z`)
  return `${WEEKDAYS[d.getUTCDay()]}, ${MONTHS[d.getUTCMonth()]} ${d.getUTCDate()}`
}

/**
 * UID-96 (C3) — which date(s) a cart line's "Ship date" cell shows.
 *
 * In `ship_as_ready` mode every line ships on its own promise, so the
 * primary date is always the line's own — nothing is ever held back.
 *
 * In `ship_together` mode the WHOLE order ships on one order-level date; a
 * line whose own promise (`CartLinePresentation.shipDate`, already the
 * line's real committed date regardless of plan — `cart-derivation.ts`
 * `deriveLinePresentation`) is earlier than that order-level date is being
 * held back to ship with the rest. That line shows the order-level date as
 * primary AND its own earlier date struck through beneath it (prototype:
 * `showReadyStrike:(_plan==='together' && _readyDt.getTime()<_shipDt.getTime())`,
 * copy "Could ship this early — deferred to ship everything together").
 *
 * Fidelity note (code-review W1): the prototype's own comparison is ship-side
 * on BOTH sides — `_readyDt` (the line's bucket ready date) vs `_shipDt` (the
 * order's ship date in `ship_together` mode, `_addD(_togDays(_speed))`,
 * `Fictiv Platform.dc.html` line ~7293/7284). This module keeps that ship-vs-
 * ship shape: the BFF exposes no literal order-level SHIP field, but the caller
 * (`CartPage`) supplies `orderShipDate` as the LATEST line ship date — the day
 * the order can actually leave once its slowest line is ready — so both sides
 * of the comparison are ship dates. An ARRIVAL estimate (`arrivalEstimateDate`,
 * ship + transit) must NOT be passed here: it would falsely strike every line
 * ready during the transit window. `orderShipDate`/`orderShipDateLabel` are
 * named for the semantic role they play, not a literal BFF field of that name.
 */
export function deriveLineDateDisplay({
  mode,
  presentation,
  orderShipDate,
  orderShipDateLabel,
}: DeriveLineDateDisplayInput): LineDateDisplay {
  const ownDate = presentation?.shipDate ?? null
  const ownDateLabel = presentation?.shipDateLabel ?? null

  if (mode !== 'ship_together' || orderShipDate === null) {
    return { primaryDateLabel: ownDateLabel }
  }

  const isHeldBack = ownDate !== null && ownDate < orderShipDate
  if (!isHeldBack) {
    return { primaryDateLabel: orderShipDateLabel }
  }

  return {
    primaryDateLabel: orderShipDateLabel,
    ...(ownDateLabel !== null ? { earlyReadyDateLabel: ownDateLabel } : {}),
  }
}
